/**
 * The reward channel, and the only one this game has.
 *
 * Until now the kart only ever punished: suppression took speed, obstacles took
 * speed, leaving the track took speed. That is a real defect in game feel — a
 * player who does everything right receives nothing, they merely avoid losing —
 * and it is the thing this fixes.
 *
 * ## Why it rewards rhythm and not blinks
 *
 * The obvious version is "every blink is a boost", and it fails twice.
 *
 * Design doc §1 has the first reason: the moment blinking is the scored action
 * the player starts controlling it, and a game that produces three minutes of
 * artificially fast blinking followed by 5/min the instant it closes has
 * achieved nothing. §2 has the shape of it — a healthy rate is one blink every
 * 3–4 seconds, so any mechanic wanting them faster is fighting physiology.
 *
 * The second reason is measured rather than argued, and it is the one that
 * settles it. Rapid voluntary blinking produces shallow, short closures, and
 * shallow short closures are precisely where this detector is worst: at 15fps a
 * 220ms blink is 3–4 samples and none is likely to land on the peak, so it
 * vanishes entirely. The first iPhone to run the game captured at 14.2fps. A
 * mechanic that asked for fast blinking would be asking for the one signal the
 * pipeline cannot see, and would feel broken in exactly the situation it
 * rewarded.
 *
 * So a blink earns credit in proportion to how *well spaced* it was. Blink at
 * your natural rate and the meter fills and stays full. Blink frantically and
 * you earn almost nothing, without ever being told off for it — the meter just
 * does not move, which is a lesson that lands without a tutorial line.
 */

/**
 * Below this gap a blink earns nothing.
 *
 * Not a punishment — an absence. Two blinks 400ms apart are one physiological
 * event as far as rhythm goes, and the second one being worth zero is what
 * stops the meter from being farmable.
 */
const FRANTIC_MS = 1200;

/** Gap at which a blink is worth full credit. */
const GOOD_MS = 2500;

/**
 * How much of the meter one perfectly spaced, fully closed blink is worth.
 *
 * Three of them fill it. That number is set by the level length rather than by
 * taste: a scenario is 30 seconds and a natural rate is one blink every 3–4
 * seconds, so a player gets roughly eight blinks in a leg. If filling the meter
 * took most of them, the reward would arrive as the leg ended and nobody would
 * connect the two.
 */
const CREDIT_PER_BLINK = 0.34;

/**
 * Drain per second, and the grace period before any of it applies.
 *
 * The first version drained continuously and was wrong in a way only a test
 * caught: a natural rhythm delivers 0.34 of credit every ~3.5s and a constant
 * 0.09/s takes 0.315 of it back, so the meter crept up by 0.025 a cycle and
 * would have needed well over two minutes to fill. In a thirty-second level it
 * would never have moved, and the reward this file exists to provide would not
 * have existed.
 *
 * Draining during the healthy interval was the mistake. Nothing is wrong during
 * those seconds — that is the definition of the healthy interval — so nothing
 * should be charged for them. The grace is `HEALTHY_INTERVAL_MS` itself, taken
 * from `SuppressionTracker` rather than restated, and the rate empties a full
 * meter exactly as suppression reaches its maximum. The two channels then agree
 * about what "you have stopped blinking" means instead of each holding its own
 * opinion about it.
 */
const DRAIN_PER_S = 1 / 8;

import { HEALTHY_INTERVAL_MS } from "../core/suppression.ts";

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * How much credit a gap of `intervalMs` is worth, 0..1.
 *
 * Exported because it is the whole argument of this file expressed as a
 * function, and a test that asserts on it is asserting on the design.
 */
export function spacingCredit(intervalMs: number): number {
  if (!Number.isFinite(intervalMs) || intervalMs <= FRANTIC_MS) return 0;
  if (intervalMs >= GOOD_MS) return 1;
  return (intervalMs - FRANTIC_MS) / (GOOD_MS - FRANTIC_MS);
}

export class RhythmMeter {
  #value = 0;
  #lastBlinkAt: number | null = null;
  #lastTickAt: number | null = null;

  /** 0..1. What the turbo is scaled by. */
  get value(): number {
    return this.#value;
  }

  /**
   * @param t frame time of the blink — the same clock the events carry.
   * @param relief 0..1, how far the lid actually closed. Design doc §5: partial
   *   blink, partial effect. A half blink earns half the credit, which is how
   *   the game teaches closing properly without a line of copy saying so.
   */
  blink(t: number, relief: number): void {
    const previous = this.#lastBlinkAt;
    this.#lastBlinkAt = t;
    // The first blink of a session has no interval to judge, and crediting it
    // fully would hand a free third of the meter to anyone who just arrived.
    if (previous === null) return;

    this.#value = clamp01(
      this.#value + CREDIT_PER_BLINK * spacingCredit(t - previous) * clamp01(relief),
    );
  }

  /**
   * Advances the meter to frame time `t`, draining only past the grace period.
   *
   * Takes absolute time rather than a delta so it can tell "3 seconds since the
   * last blink" from "11 seconds since the last blink" — the whole point of the
   * grace is that those two are charged differently, and a delta cannot
   * distinguish them.
   */
  tick(t: number): void {
    const previous = this.#lastTickAt;
    this.#lastTickAt = t;
    if (previous === null || this.#lastBlinkAt === null) return;

    // Same clamp and same reasoning as MAX_CLOSED_STEP_MS in core/metrics.ts: a
    // stalled camera leaves a hole, and charging the whole hole to idleness
    // would empty the meter for time nobody was present for.
    const dt = Math.min(t - previous, 250);
    if (dt <= 0) return;
    if (t - this.#lastBlinkAt <= HEALTHY_INTERVAL_MS) return;

    this.#value = clamp01(this.#value - (dt / 1000) * DRAIN_PER_S);
  }

  reset(): void {
    this.#value = 0;
    this.#lastBlinkAt = null;
    this.#lastTickAt = null;
  }
}

export { CREDIT_PER_BLINK, DRAIN_PER_S, FRANTIC_MS, GOOD_MS, HEALTHY_INTERVAL_MS };
